import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/formatters';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiAlertTriangle } from 'react-icons/fi';
import { login, loginTrial, loginWithOAuth, completeOAuthSignup, clearAuthError } from '../redux/actions/authActions';
import { QBASE_API } from '../config/qbase';
import { saveUser, restoreUser, purgeUser, trialAccounts } from '../services/qbaseClient';
import { readNaverCallback } from '../services/oauthClients';
import SocialLoginButtons from './SocialLoginButtons';
import { useConfirm } from './ConfirmDialog';
import './LoginPage.css';

const LoginPage = () => {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [isSignUpMode, setIsSignUpMode] = useState(false);
    const [credentials, setCredentials] = useState({
        username: '',
        password: ''
    });
    const [savePassword, setSavePassword] = useState(false);
    /**
     * 아이디/비밀번호 칸을 펼쳤는가. **기본은 접힌 상태**다(사용자 결정 2026-09-11) —
     * 소셜 로그인이 기본 수단이고, 이쪽은 기존 계정(교육용 edu01 등)을 위해 남겨 둔다.
     */
    const [showIdLogin, setShowIdLogin] = useState(false);
    const [socialError, setSocialError] = useState('');
    /**
     * 소셜로 **계정이 방금 만들어졌을 때**의 가입 마무리 단계(사용자 결정 2026-09-12).
     * `{ pending }` 이 들어 있으면 그 화면을 대신 그린다 — 아직 로그인 상태로 넘어가지 않았다.
     */
    const [signupStep, setSignupStep] = useState(null);
    const [signupNickname, setSignupNickname] = useState('');
    const [signupBusy, setSignupBusy] = useState(false);
    const [signUpData, setSignUpData] = useState({
        email: '',
        nickname: '',
        verificationCode: ''
    });
    const [verificationStep, setVerificationStep] = useState('input'); // 'input', 'verify', 'complete'
    const [mockTempPassword, setMockTempPassword] = useState(''); // 목업용 임시 비밀번호
    const [signUpLoading, setSignUpLoading] = useState(false);
    const [signUpError, setSignUpError] = useState('');
    // 복구·삭제가 끝났다는 **성공 안내**. 에러 자리에 쓰면 붉게 나와 실패로 읽힌다.
    const [signUpNotice, setSignUpNotice] = useState('');
    const [signUpSuccess, setSignUpSuccess] = useState(''); // 성공 메시지
    const [existingUserInfo, setExistingUserInfo] = useState(null); // 이미 가입된 사용자 정보
    
    // 패스워드 찾기 상태
    const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
    const [forgotPasswordData, setForgotPasswordData] = useState({
        email: ''
    });
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
    
    const dispatch = useDispatch();
    const { loading, error } = useSelector(state => state.auth);

    // 데스크톱 설치 버전 다운로드 manifest — 백엔드의 backend/public/downloads/manifest.json
    // 이 있어야 표시. 없으면 (404) 다운로드 섹션 통째 숨김.
    const [downloadManifest, setDownloadManifest] = useState(null);
    // 체험 계정 — 누르면 비밀번호 없이 그 계정으로 들어간다(서버가 대신 로그인, backend/modules/trial). 설정이 없으면 빈 목록이라 보이지 않는다
    const [trials, setTrials] = useState([]);
    const [trialPicked, setTrialPicked] = useState(null);
    useEffect(() => {
        let alive = true;
        trialAccounts().then(a => { if (alive) setTrials(a); });
        return () => { alive = false; };
    }, []);
    const tryTrial = (id) => {
        if (loading) return;
        setTrialPicked(id);
        setSocialError('');
        dispatch(loginTrial(id));
    };

    useEffect(() => {
        // Clear any existing errors when component mounts
        dispatch(clearAuthError());

        // manifest 로드 — 실패 시 그냥 표시 안 함 (조용히 fail)
        const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
        fetch(`${apiBase}/sql-developer/downloads/manifest`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data && data.available && Array.isArray(data.files)) {
                    setDownloadManifest(data);
                }
            })
            .catch(() => { /* silent — 다운로드 가능한 빌드가 없으면 섹션 숨김 */ });
    }, [dispatch]);

    // 사람이 읽기 좋은 파일 크기 (예: "12.4 MB")
    const formatBytes = (bytes) => {
        if (!bytes) return '';
        const units = ['B', 'KB', 'MB', 'GB'];
        let v = bytes;
        let u = 0;
        while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
        return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[u]}`;
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (error) {
            dispatch(clearAuthError());
        }
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!credentials.username.trim() || !credentials.password.trim()) {
            return;
        }
        
        dispatch(login(credentials.username.trim(), credentials.password.trim()));
    };
    

    const handleSignUpInputChange = (e) => {
        const { name, value } = e.target;
        setSignUpData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 입력 시 에러/성공 메시지 초기화
        if (signUpError) {
            setSignUpError('');
        }
        if (signUpSuccess) {
            setSignUpSuccess('');
        }
    };

    // 목업용 유틸리티 함수들
    const generateMockVerificationCode = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    const generateMockTempPassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$';
        let result = '';
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const simulateEmailSending = () => {
        return new Promise((resolve) => {
            setTimeout(resolve, 2000); // 2초 지연으로 이메일 발송 시뮬레이션
        });
    };

    /**
     * 탈회 계정 재가입 — 되살릴지, 지우고 새로 만들지 고르게 한다.
     *
     * 영구 삭제는 되돌릴 수 없으므로 한 번 더 묻는다. 어느 쪽이든 **끝나면 무엇을 해야 하는지**
     * 를 말해 준다 — 복구는 바로 로그인, 삭제는 가입을 다시.
     *
     * @returns {Promise<boolean>} 화면이 안내를 표시했으면 true (일반 에러 문구를 덮어쓰지 않게)
     */
    const handleWithdrawn = async (info) => {
        const restore = await confirm({
            title: t('auth:withdrawn.title'),
            message: t('auth:withdrawn.message', { email: info.email || signUpData.email }),
            confirmText: t('auth:withdrawn.restore'),
            cancelText: t('auth:withdrawn.purge'),
        });

        if (restore) {
            try {
                await restoreUser(info.usrid);
                setSignUpError('');
                setSignUpNotice(t('auth:withdrawn.restored'));
                return true;
            } catch (err) {
                setSignUpError(t('auth:withdrawn.restoreFailed', { message: err.message }));
                return true;
            }
        }

        // 영구 삭제는 되돌릴 수 없다 — 한 번 더 확인한다
        const sure = await confirm({
            title: t('auth:withdrawn.purgeTitle'),
            message: t('auth:withdrawn.purgeConfirm'),
            confirmText: t('auth:withdrawn.purgeGo'),
            cancelText: t('common:action.cancel'),
            variant: 'danger',
        });
        if (!sure) {
            setSignUpError(t('auth:withdrawn.cancelled'));
            return true;
        }
        try {
            await purgeUser(info.usrid);
            setSignUpError('');
            setSignUpNotice(t('auth:withdrawn.purged'));
        } catch (err) {
            setSignUpError(t('auth:withdrawn.purgeFailed', { message: err.message }));
        }
        return true;
    };

    /**
     * 소셜 로그인의 **뒤처리 한 곳.** Google 은 팝업에서 바로, Naver 는 리다이렉트로 돌아온
     * 뒤에 들어온다. 탈회 계정이면 QBase 가 실패가 아니라 «복구할까, 지울까» 를 묻는데,
     * 그 갈래를 아이디 가입과 **같은 대화상자**로 처리한다 — 화면마다 다르면 사용자가
     * 어느 쪽에서 막혔는지에 따라 다른 안내를 받는다.
     *
     * 실패 문구는 **여기서 따로 들고 있는다.** redux 의 `error` 는 아이디/비밀번호 폼 안에서
     * 그려지는데 그 폼은 기본으로 접혀 있어서, 소셜 실패를 그쪽에 맡기면 **아무 말도 없이
     * 아무 일도 안 일어난 것처럼** 보인다. 대신 redux 쪽은 비워 둔다 — 폼을 펼쳤을 때 같은
     * 문구가 두 번 나오지 않게.
     */
    const finishSocial = async (provider, payload) => {
        setSocialError('');
        const r = await dispatch(loginWithOAuth(provider, payload));
        if (r && r.ok) {
            // **처음 온 사람**이면 바로 들여보내지 않고 가입 마무리를 거친다. 지금까지는
            // 닉네임이 구글 이름으로 정해진 채 조용히 가입돼, 사용자는 가입된 사실도 몰랐다.
            if (r.created && r.pending) {
                setSignupNickname((r.pending.user && r.pending.user.name) || '');
                setSignupStep({ provider, pending: r.pending });
            }
            return;
        }
        const w = r && r.body;
        if (w && w.withdrawn && w.usrid) {
            dispatch(clearAuthError());
            await handleWithdrawn({ usrid: w.usrid, email: w.emlAddr });
            return;
        }
        setSocialError(t('auth:social.failed', {
            message: (r && r.error && r.error.message) || t('auth:err.loginFailed'),
        }));
        dispatch(clearAuthError());
    };

    /**
     * 네이버에서 돌아왔는가. 주소에 `?code=…&state=…` 가 있으면 그 자리에서 로그인을 잇는다.
     * (LiveSQL 에는 라우터가 없어서 콜백 전용 경로를 둘 수 없다 — 로그인 화면이 곧 콜백이다)
     *
     * **한 번만 쓴다.** `readNaverCallback` 이 값을 꺼내면서 주소를 지우므로, 새로고침해도
     * 이미 소모된 code 로 다시 시도하지 않는다(그러면 "이미 사용된 코드" 로 실패한다).
     * StrictMode 의 이중 실행도 두 번째에는 빈손이 되어 안전하다.
     */
    useEffect(() => {
        const cb = readNaverCallback();
        if (!cb) return;
        if (cb.error) {
            setSocialError(cb.error === 'STATE_MISMATCH'
                ? t('auth:social.stateMismatch')
                : t('auth:social.failed', { message: cb.error }));
            return;
        }
        finishSocial('naver', cb);
        // 주소의 code 는 **한 번만** 쓰는 값이라 이 효과는 기동 시 한 번만 돈다.
        // finishSocial 을 의존에 넣으면 렌더마다 다시 돌려다 이미 지워진 주소를 읽는다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * 「시작하기」 — 닉네임을 확정하고 그때서야 앱으로 들어간다.
     *
     * 닉네임 저장이 실패해도 **로그인은 시킨다**(action 이 그렇게 한다). 이름 하나 때문에
     * 가입한 사람을 로그인 화면에 붙들어 두면, 계정은 이미 있는데 들어갈 수가 없다.
     */
    const submitSignup = async (e) => {
        e.preventDefault();
        setSignupBusy(true);
        try {
            const r = await dispatch(completeOAuthSignup({
                nickname: signupNickname, pending: signupStep.pending,
            }));
            if (r && r.noticed) toast.warning(t('auth:social.nicknameFailed', { message: r.noticed }));
        } finally {
            setSignupBusy(false);
        }
    };

    const handleSendVerification = async (e) => {
        e.preventDefault();
        
        if (!signUpData.email.trim() || !signUpData.nickname.trim()) {
            setSignUpError(t('auth:err.emailAndNicknameRequired'));
            return;
        }

        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(signUpData.email)) {
            setSignUpError(t('auth:err.emailInvalid'));
            return;
        }

        // 닉네임 길이 검증
        if (signUpData.nickname.length < 2) {
            setSignUpError(t('auth:err.nicknameTooShort'));
            return;
        }

        setSignUpLoading(true);
        setSignUpError('');

        try {
            // Node 프록시가 아니라 QBase 를 직접 부른다 — 같은 엔드포인트가 QBase 에 있다
            // (qcore/syscmm/emailuser/EmailVerifController). 프록시는 중간에서 서비스계정으로
            // 갈아타고 있었을 뿐, 실제 구현은 처음부터 저쪽이었다.
            const response = await fetch(`${QBASE_API}/api/emailverif/send-verif-code`, {
                credentials: 'include',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: signUpData.email,
                    nickname: signUpData.nickname
                })
            });

            const result = await response.json();
            console.log('API 응답:', { status: response.status, ok: response.ok, result });
            
            // HTTP 에러 상태코드 처리
            if (!response.ok) {
                console.error('HTTP 에러 응답:', result);
                
                // result.status가 있으면 그걸 사용, 없으면 HTTP 상태코드 기반 처리
                if (result.status === 'NICKNAME_ALREADY_EXISTS' || 
                    (result.message && result.message.includes(t('auth:signup.nickname')))) {
                    setSignUpError(result.message || t('auth:err.nicknameTaken'));
                    return;
                } else if (result.status === 'ALREADY_VERIFIED') {
                    setSignUpError(result.message || t('auth:err.alreadyVerified'));
                    setExistingUserInfo({ email: signUpData.email, nickname: signUpData.nickname });
                    return;
                } else {
                    setSignUpError(result.message || t('auth:err.sendFailed'));
                    return;
                }
            }

            if (result.success) {
                // 성공 처리
                console.log('✅ 인증 코드 발송 성공:', result);
                
                // 임시 패스워드 저장 (자동 로그인용)
                if (result.tempPassword) {
                    setMockTempPassword(result.tempPassword);
                    sessionStorage.setItem('tempPassword', result.tempPassword);
                    sessionStorage.setItem('userEmail', result.email);
                    console.log('🔐 임시 패스워드 저장됨:', result.tempPassword);
                }
                
                // 상태별 UI 처리
                switch (result.status) {
                    case 'NEW_EMAIL':
                        setSignUpError(''); // 에러 메시지 클리어
                        setSignUpSuccess(result.message);
                        setVerificationStep('verify');
                        break;
                        
                    case 'RESEND_CODE':
                        setSignUpError(''); // 에러 메시지 클리어
                        setSignUpSuccess(result.message);
                        setVerificationStep('verify');
                        break;
                        
                    default:
                        setSignUpError('');
                        setSignUpSuccess(result.message);
                        setVerificationStep('verify');
                        break;
                }
                
            } else {
                // 실패 처리
                console.error('❌ 인증 코드 발송 실패:', result);
                
                switch (result.status) {
                    case 'ALREADY_VERIFIED':
                        if (result.action === 'LOGIN_REQUIRED') {
                            setSignUpError(result.message);
                            setExistingUserInfo({ email: signUpData.email, nickname: signUpData.nickname });
                            console.log('이미 인증 완료된 이메일:', signUpData.email);
                        }
                        break;
                        
                    case 'NICKNAME_ALREADY_EXISTS':
                        console.log('닉네임 중복 에러:', result.message);
                        setSignUpError(result.message);
                        // 닉네임 필드에 포커스 (닉네임만 다시 입력하도록)
                        break;
                        
                    case 'CORE_SERVER_ERROR':
                        console.error('코어서버 에러:', result.message);
                        setSignUpError(result.message || t('auth:err.server'));
                        break;
                        
                    default:
                        setSignUpError(result.message || t('auth:err.sendFailed'));
                        break;
                }
                return;
            }

        } catch (error) {
            console.error('인증 메일 발송 오류:', error);
            
            // 네트워크 에러나 서버 응답 파싱 에러 처리
            if (error.message.includes('NICKNAME_ALREADY_EXISTS') || 
                error.message.includes(t('auth:signup.nickname'))) {
                setSignUpError(t('auth:err.nicknameTaken'));
            } else if (error.message.includes('ALREADY_VERIFIED')) {
                setSignUpError(t('auth:err.alreadyVerified'));
                setExistingUserInfo({ email: signUpData.email, nickname: signUpData.nickname });
            } else {
                setSignUpError(error.message || t('auth:err.sendError'));
            }
        } finally {
            setSignUpLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        
        if (!signUpData.verificationCode.trim()) {
            setSignUpError(t('auth:err.codeRequired'));
            return;
        }

        if (signUpData.verificationCode.length !== 6) {
            setSignUpError(t('auth:err.codeFormat'));
            return;
        }

        setSignUpLoading(true);
        setSignUpError('');

        try {
            const requestData = {
                email: signUpData.email,
                verificationCode: signUpData.verificationCode
            };
            
            console.log('[Frontend] 검증 코드 전송 요청:');
            console.log('  - 이메일:', requestData.email);
            console.log('  - 검증코드:', requestData.verificationCode);
            console.log('  - 검증코드 타입:', typeof requestData.verificationCode);
            console.log('  - 검증코드 길이:', requestData.verificationCode?.length);
            console.log('  - 전송 데이터:', JSON.stringify(requestData, null, 2));
            
            const response = await fetch(`${QBASE_API}/api/emailverif/verify-code`, {
                credentials: 'include',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            const result = await response.json();
            
            console.log('[Frontend] 백엔드 응답 받음:');
            console.log('  - 응답 상태:', response.status);
            console.log('  - 응답 데이터:', JSON.stringify(result, null, 2));

            if (!response.ok) {
                console.error('[Frontend] 응답 에러:', result.error);
                throw new Error(result.error || t('auth:err.codeVerifyFailed'));
            }

            console.log('✅ [Frontend] 인증 성공:', result.message);
            console.log('🔐 [Frontend] 임시 비밀번호 확인:', result.tempPassword);
            
            if (!result.tempPassword) {
                console.error('[Frontend] ❌ tempPassword가 없습니다!');
                setSignUpError(t('auth:err.noTempPassword'));
                return;
            }
            
            // 임시 비밀번호 저장
            setMockTempPassword(result.tempPassword);
            sessionStorage.setItem('tempPassword', result.tempPassword);
            sessionStorage.setItem('userEmail', result.email);

            console.log('✅ [Frontend] 임시 비밀번호 상태 저장 완료');

            // 테넌트 계정 생성 — 이메일 인증은 공통 저장소(VW_EML_USER·VW_USER)에만 계정을 만들고,
            // 로그인은 테넌트 저장소(VW_QBASE_USER)를 본다. 이 호출이 빠지면 가입은 성공하는데
            // 곧바로 로그인이 실패한다.
            try {
                await saveUser({
                    loginId: result.email,
                    pwd: result.tempPassword,
                    userKnme: signUpData.nickname || result.email,
                    emlAddr: result.email,
                });
                console.log('✅ [Frontend] 테넌트 계정 생성 완료');
            } catch (tenantError) {
                // **탈회했던 계정으로 다시 가입하는 경우.**
                //
                // QBase 는 이때 실패가 아니라 **선택을 요구**한다(withdrawn: true) — 예전 데이터를
                // 되살릴지, 지우고 새로 시작할지. 그런데 여기서 그냥 에러로 끝내면 이메일 인증은
                // 이미 통과해 공통 계정과 임시 비밀번호가 생긴 뒤라, 사용자는 비밀번호를 받았는데
                // 로그인은 안 되는 막다른 길에 갇힌다. 다시 가입해도 같은 자리에 선다.
                const w = tenantError.body;
                if (w && w.withdrawn && w.usrid) {
                    const handled = await handleWithdrawn(w);
                    if (handled) return;
                }
                console.error('[Frontend] 테넌트 계정 생성 실패:', tenantError.message);
                setSignUpError(
                    t('auth:err.tenantCreateFailed', { message: tenantError.message })
                );
                return;
            }
            
            // 상태별 처리
            switch (result.status) {
                case 'VERIFICATION_COMPLETED':
                    setVerificationStep('complete');
                    break;
                default:
                    setVerificationStep('complete');
                    break;
            }

        } catch (error) {
            console.error('인증 오류:', error);
            setSignUpError(error.message || t('auth:err.verifyError'));
        } finally {
            setSignUpLoading(false);
        }
    };

    const toggleMode = () => {
        setIsSignUpMode(!isSignUpMode);
        setIsForgotPasswordMode(false); // 패스워드 찾기 모드 해제
        setVerificationStep('input');
        setSignUpData({
            email: '',
            nickname: '',
            verificationCode: ''
        });
        setSignUpError('');
        setSignUpSuccess('');
        setSignUpLoading(false);
        setMockTempPassword('');
        setExistingUserInfo(null);
        // 패스워드 찾기 관련 상태도 초기화
        setForgotPasswordData({ email: '' });
        setForgotPasswordError('');
        setForgotPasswordSuccess('');
        setForgotPasswordLoading(false);
        dispatch(clearAuthError());
    };

    // 이미 가입된 이메일로 로그인 화면으로 이동
    const goToLoginWithEmail = () => {
        if (existingUserInfo) {
            setIsSignUpMode(false);
            setCredentials({
                username: existingUserInfo.email,
                password: ''
            });
            setExistingUserInfo(null);
            setSignUpError('');
        }
    };

    // 이메일 로그인으로 전환
    const handleEmailLogin = () => {
        // sessionStorage에서 임시 패스워드 가져오기
        const storedEmail = sessionStorage.getItem('userEmail');
        const storedTempPassword = sessionStorage.getItem('tempPassword');
        
        setIsSignUpMode(false);
        setCredentials({
            username: storedEmail || signUpData.email,
            password: storedTempPassword || mockTempPassword
        });
        
        console.log('🔐 [Frontend] 로그인 화면으로 이동:', {
            email: storedEmail || signUpData.email,
            hasTempPassword: !!(storedTempPassword || mockTempPassword)
        });
    };
    
    // 패스워드 찾기 관련 핸들러들
    const handleForgotPasswordInputChange = (e) => {
        const { name, value } = e.target;
        setForgotPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 에러 메시지 클리어
        if (forgotPasswordError) {
            setForgotPasswordError('');
        }
    };
    
    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        
        if (!forgotPasswordData.email.trim()) {
            setForgotPasswordError(t('auth:err.emailRequired'));
            return;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(forgotPasswordData.email)) {
            setForgotPasswordError(t('auth:err.emailInvalid'));
            return;
        }
        
        setForgotPasswordLoading(true);
        setForgotPasswordError('');
        setForgotPasswordSuccess('');
        
        try {
            const response = await fetch(`${QBASE_API}/api/emailverif/issue-temp-password`, {
                credentials: 'include',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: forgotPasswordData.email
                })
            });
            
            const result = await response.json();
            console.log('패스워드 찾기 API 응답:', { status: response.status, ok: response.ok, result });
            
            if (response.ok && result.success) {
                console.log('✅ 임시 패스워드 발급 성공:', result);
                setForgotPasswordSuccess(result.message);
                setForgotPasswordError('');

                // 폴백 모드에서 임시패스워드가 직접 제공된 경우 자동 입력
                const autoPassword = result.tempPassword || '';

                // 3초 후에 로그인 화면으로 이동 (임시패스워드 자동 입력)
                setTimeout(() => {
                    setIsForgotPasswordMode(false);
                    setCredentials({
                        username: forgotPasswordData.email,
                        password: autoPassword
                    });
                    setForgotPasswordSuccess('');
                    setForgotPasswordData({ email: '' });
                }, 3000);
                
            } else {
                console.error('❌ 임시 패스워드 발급 실패:', result);
                
                // 백엔드 응답을 가리는 판별용 원문 — 번역하면 판별이 깨진다.
                if (response.status === 404 || result.message?.includes('등록되지 않은')) {
                    setForgotPasswordError(t('auth:err.notRegistered'));
                } else {
                    setForgotPasswordError(result.message || t('auth:err.issueFailed'));
                }
            }
        } catch (error) {
            console.error('패스워드 찾기 오류:', error);
            setForgotPasswordError(error.message || t('auth:err.issueError'));
        } finally {
            setForgotPasswordLoading(false);
        }
    };
    
    const toggleForgotPasswordMode = () => {
        setIsForgotPasswordMode(!isForgotPasswordMode);
        setForgotPasswordData({ email: '' });
        setForgotPasswordError('');
        setForgotPasswordSuccess('');
        setForgotPasswordLoading(false);
        dispatch(clearAuthError());
    };
    
    const backToLogin = () => {
        setIsForgotPasswordMode(false);
        setIsSignUpMode(false);
        setForgotPasswordData({ email: '' });
        setForgotPasswordError('');
        setForgotPasswordSuccess('');
        setForgotPasswordLoading(false);
        dispatch(clearAuthError());
    };
    
    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <img
                        src={`${process.env.PUBLIC_URL}/${
                            (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark')
                                ? 'logo1_dark.png'
                                : 'logo1.png'
                        }`}
                        alt="LiveSQL"
                        className="login-logo"
                    />
                </div>
                
                {signupStep ? (
                    /*
                      가입 마무리 — 계정은 방금 만들어졌고(QBase 가 자동 가입시킨다) 세션도 있다.
                      **아직 로그인 상태로 넘기지 않은** 이유는, 넘기는 순간 App 이 본 화면으로
                      바뀌어 이 단계가 뜰 자리가 없어지기 때문이다.

                      묻는 것은 닉네임 하나다. 지금까지는 구글/네이버가 준 이름이 그대로 박혔고
                      사용자는 가입된 사실도, 그 이름이 남에게 보인다는 것도 몰랐다.
                    */
                    <form className="login-form signup-oauth" onSubmit={submitSignup}>
                        <div className="signup-oauth-title">{t('auth:social.welcomeTitle')}</div>
                        <div className="signup-oauth-desc">{t('auth:social.welcomeDesc')}</div>

                        <div className="form-group">
                            <label htmlFor="oauth-nickname" className="form-label">
                                {t('auth:social.nickname')}
                            </label>
                            <input
                                type="text"
                                id="oauth-nickname"
                                className="form-input"
                                value={signupNickname}
                                onChange={(e) => setSignupNickname(e.target.value)}
                                maxLength={30}
                                disabled={signupBusy}
                                autoFocus
                            />
                            <div className="signup-oauth-hint">{t('auth:social.nicknameHint')}</div>
                        </div>

                        {/* 이메일은 제공자가 정한 값이라 여기서 바꿀 수 없다 — 바꿀 수 있는 것처럼
                            입력칸을 두면 고쳐 놓고 왜 안 바뀌냐고 묻게 된다 */}
                        <div className="form-group">
                            <span className="form-label">{t('auth:social.email')}</span>
                            <div className="signup-oauth-fixed">
                                {(signupStep.pending.user && signupStep.pending.user.email) || ''}
                                <span className="signup-oauth-via">
                                    {t(`auth:social.via.${signupStep.provider}`)}
                                </span>
                            </div>
                        </div>

                        <div className="button-group">
                            <button
                                type="submit"
                                className="login-button primary"
                                disabled={signupBusy || !signupNickname.trim()}
                            >
                                {signupBusy ? t('auth:login.signingIn') : t('auth:social.start')}
                            </button>
                        </div>
                    </form>
                ) : isForgotPasswordMode ? (
                    // 패스워드 찾기 폼
                    <div className="forgot-password-form">
                        <form onSubmit={handleForgotPasswordSubmit}>
                            <div className="form-group">
                                <label htmlFor="forgot-email" className="form-label">
                                    {t('auth:login.email')}
                                </label>
                                <input
                                    type="email"
                                    id="forgot-email"
                                    name="email"
                                    value={forgotPasswordData.email}
                                    onChange={handleForgotPasswordInputChange}
                                    className="form-input"
                                    placeholder={t('auth:signup.registeredEmailPlaceholder')}
                                    disabled={forgotPasswordLoading}
                                    autoComplete="email"
                                    required
                                />
                            </div>
                            
                            <div className="button-group">
                                <button
                                    type="submit"
                                    className="login-button primary"
                                    disabled={forgotPasswordLoading || !forgotPasswordData.email.trim()}
                                >
                                    {forgotPasswordLoading ? t('auth:forgot.issuing') : t('auth:forgot.issue')}
                                </button>
                            </div>
                        </form>
                        
                        {forgotPasswordError && (
                            <div className="error-message">
                                <div className="error-icon"><FiAlertTriangle size={14} /></div>
                                <div className="error-text">{forgotPasswordError}</div>
                            </div>
                        )}
                        
                        {forgotPasswordSuccess && (
                            <div className="success-message">
                                <div className="success-icon">✅</div>
                                <div className="success-text">
                                    {forgotPasswordSuccess}
                                    <br />
                                    <small>{t('auth:signup.redirecting')}</small>
                                </div>
                            </div>
                        )}
                        
                        <div className="auth-toggle">
                            <button
                                type="button"
                                className="toggle-link"
                                onClick={backToLogin}
                            >
                                {t('auth:login.backToLogin')}
                            </button>
                        </div>
                    </div>
                ) : !isSignUpMode ? (
                    // 로그인 — **소셜이 기본**이고 아이디/비밀번호는 접어 둔다(사용자 결정 2026-09-11)
                    <div className="login-form">
                        <SocialLoginButtons
                            onGoogleToken={(token, err) => {
                                if (token) finishSocial('google', { accessToken: token });
                                else if (err) setSocialError(t('auth:social.failed', { message: err.message }));
                            }}
                            busy={loading}
                            error={socialError}
                        />

                        {trials.length > 0 && (
                            <div className="login-trial">
                                <div className="login-trial-title">{t('auth:trial.title')}</div>
                                <div className="login-trial-list">
                                    {trials.map(id => (
                                        <button key={id} type="button" className="login-trial-btn" onClick={() => tryTrial(id)} disabled={loading}
                                            title={t('auth:trial.pickTitle', { id })}>
                                            {loading && trialPicked === id ? t('auth:trial.checking') : id}
                                        </button>
                                    ))}
                                </div>
                                <div className="login-trial-hint">{t('auth:trial.hint')}</div>
                            </div>
                        )}

                        <div className="login-or"><span>{t('auth:social.or')}</span></div>

                        {!showIdLogin ? (
                            <button
                                type="button" className="login-button secondary login-idToggle"
                                onClick={() => setShowIdLogin(true)}
                            >
                                {t('auth:social.useId')}
                            </button>
                        ) : (
                        <form className="login-idForm" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="username" className="form-label">
                                    {t('auth:login.emailUser')}
                                </label>
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    value={credentials.username}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder="user@mail.com"
                                    disabled={loading}
                                    autoComplete="username"
                                />
                            </div>
                        
                            <div className="form-group">
                                <label htmlFor="password" className="form-label">
                                    {t('auth:login.password')}
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={credentials.password}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder={t('auth:login.passwordPlaceholder')}
                                    disabled={loading}
                                    autoComplete="current-password"
                                />
                            </div>
                        
                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={savePassword}
                                        onChange={(e) => setSavePassword(e.target.checked)}
                                        className="checkbox-input"
                                    />
                                    <span className="checkbox-text">{t('auth:login.remember')}</span>
                                </label>
                            </div>
                        
                            {error && (
                                <div className="error-message">
                                    <div className="error-icon"><FiAlertTriangle size={14} /></div>
                                    <div className="error-text">{error}</div>
                                </div>
                            )}
                        
                            <div className="button-group">
                                <button
                                    type="submit"
                                    className="login-button primary"
                                    disabled={loading || !credentials.username.trim() || !credentials.password.trim()}
                                >
                                    {loading ? t('auth:login.signingIn') : t('auth:login.title')}
                                </button>
                            
                            </div>
                        
                            <div className="forgot-password-link">
                                <button
                                    type="button"
                                    className="link-button"
                                    onClick={toggleForgotPasswordMode}
                                >
                                    {t('auth:login.forgot')}
                                </button>
                            </div>
                        </form>
                        )}
                    </div>
                ) : (
                    // 회원가입 폼
                    <div className="signup-form">
                        {verificationStep === 'input' && (
                            <form onSubmit={handleSendVerification}>
                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">
                                        {t('auth:login.emailAsUser')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={signUpData.email}
                                        onChange={handleSignUpInputChange}
                                        className="form-input"
                                        placeholder={t('auth:login.emailPlaceholder')}
                                        required
                                        autoComplete="email"
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="nickname" className="form-label">
                                        {t('auth:signup.nickname')}
                                    </label>
                                    <input
                                        type="text"
                                        id="nickname"
                                        name="nickname"
                                        value={signUpData.nickname}
                                        onChange={handleSignUpInputChange}
                                        className="form-input"
                                        placeholder={t('auth:signup.nicknamePlaceholder')}
                                        required
                                    />
                                </div>
                                
                                <div className="button-group">
                                    <button
                                        type="submit"
                                        className="login-button primary"
                                        disabled={signUpLoading || !signUpData.email.trim() || !signUpData.nickname.trim()}
                                    >
                                        {signUpLoading ? t('auth:signup.sending') : t('auth:signup.sendCode')}
                                    </button>
                                </div>
                            </form>
                        )}
                        
                        {verificationStep === 'verify' && (
                            <form onSubmit={handleVerifyCode}>
                                <div className="verification-info">
                                    <div className="info-message">
                                        <div className="info-icon">📧</div>
                                        <div className="info-text">
                                            <p>{t('auth:signup.codeSentTo', { email: signUpData.email })}</p>
                                            <p>{t('auth:signup.checkMailbox')}</p>
                                        </div>
                                    </div>
                                    {signUpSuccess && (
                                        <div className="success-message">
                                            <div className="success-icon">✅</div>
                                            <div className="success-text">{signUpSuccess}</div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="verificationCode" className="form-label">
                                        {t('auth:signup.code')}
                                    </label>
                                    <input
                                        type="text"
                                        id="verificationCode"
                                        name="verificationCode"
                                        value={signUpData.verificationCode}
                                        onChange={handleSignUpInputChange}
                                        className="form-input"
                                        placeholder={t('auth:signup.codePlaceholder')}
                                        maxLength={6}
                                        required
                                    />
                                </div>
                                
                                <div className="button-group">
                                    <button
                                        type="submit"
                                        className="login-button primary"
                                        disabled={signUpLoading || !signUpData.verificationCode.trim()}
                                    >
                                        {signUpLoading ? t('auth:signup.verifying') : t('auth:signup.verify')}
                                    </button>
                                    
                                    <button
                                        type="button"
                                        className="login-button secondary"
                                        disabled={signUpLoading}
                                        onClick={() => setVerificationStep('input')}
                                    >
                                        {t('auth:signup.reenter')}
                                    </button>
                                </div>
                            </form>
                        )}
                        
                        {verificationStep === 'complete' && (
                            <div className="complete-message">
                                <div className="success-icon">🎉</div>
                                <h3>{t('auth:signup.done')}</h3>
                                <p>
                                    {t('auth:signup.accountCreated', { nickname: signUpData.nickname })}
                                </p>
                                <p>{t('auth:signup.canSignInNow')}</p>
                                
                                <div className="complete-info">
                                    <div className="credentials">
                                        <div className="credentials-icon">🔐</div>
                                        <div className="credentials-text">
                                            <p><strong>{t('auth:signup.credentials')}</strong></p>
                                            <p>{t('auth:signup.emailLabel')} {signUpData.email}</p>
                                            <p>{t('auth:signup.tempPasswordLabel')} {mockTempPassword}</p>
                                            <p className="info-note">{t('auth:signup.changeAfterLogin')}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="button-group">
                                    <button
                                        type="button"
                                        className="login-button primary"
                                        onClick={handleEmailLogin}
                                    >
                                        {t('auth:login.toLogin')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* 처리 완료 안내 — 탈회 계정 복구·삭제처럼 다음 할 일을 알려 줘야 하는 경우 */}
                {isSignUpMode && signUpNotice && (
                    <div className="notice-message">{signUpNotice}</div>
                )}

                {/* 에러 메시지 표시 */}
                {isSignUpMode && signUpError && (
                    <div className="error-message">
                        <div className="error-icon"><FiAlertTriangle size={14} /></div>
                        <div className="error-text">{signUpError}</div>
                        {existingUserInfo && (
                            <div className="error-actions">
                                <button
                                    type="button"
                                    className="login-button secondary"
                                    onClick={goToLoginWithEmail}
                                >
                                    {t('auth:login.goToLogin')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
                <div className="login-footer">
                    {!isSignUpMode ? (
                        <>
                            <p className="footer-text">
                                {t('auth:login.tagline')}
                            </p>
                            <div className="auth-toggle">
                                <button
                                    type="button"
                                    className="toggle-link"
                                    onClick={toggleMode}
                                >
                                    {t('auth:login.noAccountPre')} <strong>{t('auth:signup.title')}</strong>
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="auth-toggle">
                            <button
                                type="button"
                                className="toggle-link"
                                onClick={toggleMode}
                            >
                                {t('auth:login.haveAccountPre')} <strong>{t('auth:login.title')}</strong>
                            </button>
                        </div>
                    )}
                </div>

                {/* 데스크톱 설치 버전 다운로드 — manifest 가 있을 때만 노출. 카드 맨 아래 위치. */}
                {downloadManifest && downloadManifest.files && downloadManifest.files.length > 0 && (
                    <div className="login-downloads">
                        <div className="login-downloads-title">
                            {t('auth:download.title')}
                            {downloadManifest.version && <span className="login-downloads-version"> v{downloadManifest.version}</span>}
                        </div>
                        <div className="login-downloads-list">
                            {downloadManifest.files.filter(f => f.exists).map(f => {
                                const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
                                const url = `${apiBase}/sql-developer/downloads/${encodeURIComponent(f.filename)}`;
                                return (
                                    <a key={f.filename} href={url} className="login-download-link" download>
                                        <span className="login-download-label">{f.label || f.filename}</span>
                                        {f.size > 0 && <span className="login-download-size">({formatBytes(f.size)})</span>}
                                        {f.downloadCount > 0 && (
                                            <span className="login-download-count" title={f.lastDownloadAt ? t('auth:download.lastAt', { when: formatDateTime(f.lastDownloadAt) }) : undefined}>
                                                ⬇ {f.downloadCount.toLocaleString()}
                                            </span>
                                        )}
                                        {f.description && <span className="login-download-desc">{f.description}</span>}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginPage;